import React, { useState, useEffect, useRef } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../firebase.config";
import { Toaster, toast } from "react-hot-toast";

// Self-contained, custom-styled searchable dropdown to prevent alignment/CSS issues in modals
const InlineSearchableDropdown = ({ options = [], value = "", onChange, placeholder = "Select course..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch(value || "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value]);

  const filteredOptions = options.filter(option =>
    (option || "").toLowerCase().includes((search || "").toLowerCase())
  );

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center", width: "100%" }}>
        <input
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          style={{
            width: "100%",
            padding: "8px 36px 8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "14px",
            color: "#000",
            backgroundColor: "#fff",
            outline: "none",
            boxSizing: "border-box"
          }}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: "absolute",
            right: "1px",
            top: "1px",
            bottom: "1px",
            width: "32px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            outline: "none"
          }}
        >
          <i className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} style={{ fontSize: "11px" }}></i>
        </button>
      </div>

      {isOpen && (
        <ul style={{
          position: "absolute",
          zIndex: 1000,
          width: "100%",
          marginTop: "4px",
          maxHeight: "180px",
          overflowY: "auto",
          background: "#fff",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          padding: "4px 0",
          margin: 0,
          listStyle: "none",
          textAlign: "left"
        }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <li
                key={idx}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  cursor: "pointer",
                  color: "#000",
                  backgroundColor: option === value ? "#f0f9ff" : "transparent",
                  fontWeight: option === value ? "600" : "normal",
                  transition: "background-color 0.15s ease"
                }}
                onMouseEnter={(e) => {
                  if (option !== value) {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (option !== value) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {option}
              </li>
            ))
          ) : (
            <li style={{ padding: "8px 12px", fontSize: "14px", color: "#9ca3af", fontStyle: "italic" }}>
              No courses found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

// Define fields mapping per query type to ensure all relevant fields show up in edit form
const queryFieldsMap = {
  nameChange: ["name", "new_name", "email", "number", "query_desc", "comment", "file"],
  batchShift: ["name", "email", "number", "amount", "current_batch", "new_batch", "reason", "query_desc", "comment", "file"],
  emi: ["name", "email", "number", "current_course", "first_installment", "second_installment", "query_desc", "comment", "file"],
  refund: ["name", "email", "number", "current_course", "reason", "query_desc", "comment", "file"],
  removeCourseAccess: ["name", "email", "number", "current_course", "reason", "query_desc", "comment", "file"],
  feedback: ["name", "email", "number", "current_course", "feedback", "query_desc", "comment", "file"],
  numberchange: ["name", "email", "number", "new_number", "query_desc", "comment", "file"],
  emailchange: ["name", "email", "new_email", "number", "query_desc", "comment", "file"],
  contentmissing: ["name", "email", "number", "current_course", "content_desc", "query_desc", "comment", "file"],
  coursenotvisible: ["name", "email", "number", "current_course", "content_desc", "query_desc", "comment", "file"],
  UPIpayment: ["name", "email", "number", "amount", "utr", "student_user_id", "newCourseId", "current_course", "new_course", "query_desc", "comment", "file"],
  grpnotalloted: ["name", "email", "number", "current_course", "query_desc", "comment", "file"],
  technicalIssue: ["name", "email", "number", "current_course", "deviceName", "deviceSpecs", "query_desc", "comment", "file"],
  misc: ["name", "email", "number", "current_course", "new_course", "query_desc", "comment", "file"]
};

const EditQueryModal = ({ isOpen, onClose, onSave, rowData, columnMapping = {} }) => {
  const [formData, setFormData] = useState({});
  const [fileObj, setFileObj] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (rowData) {
      setFormData({ ...rowData });
      setFileObj(null);
    }
  }, [rowData]);

  useEffect(() => {
    const fetchCoursesList = async () => {
      try {
        const isLocal = window.location.hostname === "localhost";
        const base = isLocal ? "http://localhost:3000" : "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod";
        const response = await fetch(`${base}/api/courses`);
        if (response.ok) {
          const data = await response.json();
          setCourses(data.map(c => c.course_name));
        }
      } catch (err) {
        console.error("Error fetching courses list:", err);
      }
    };
    if (isOpen) {
      fetchCoursesList();
    }
  }, [isOpen]);

  if (!isOpen || !rowData) return null;

  const queryType = rowData.query_type || "misc";
  const editableFields = queryFieldsMap[queryType] || queryFieldsMap["misc"];

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const uploadFiletoCloud = async () => {
    if (!fileObj) return null;
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const dayTime = `${now.getDate()}/${now.toLocaleTimeString("en-IN", {
        hour12: false,
    })}`;
    const storageRef = ref(
        storage,
        `${monthYear}/${dayTime}-${fileObj.name}`
    );

    try {
        setUploading(true);
        toast.loading("Uploading new file...", { id: "modal-upload" });

        await uploadBytes(storageRef, fileObj);
        const fileurl = await getDownloadURL(storageRef);

        toast.dismiss("modal-upload");
        toast.success("File uploaded successfully!");
        return fileurl;
    } catch (err) {
        console.error(err);
        toast.dismiss("modal-upload");
        toast.error("Problem while uploading file");
        throw err;
      } finally {
        setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let fileUrl = formData.file;
      if (fileObj) {
        fileUrl = await uploadFiletoCloud();
      }
      onSave({
        ...formData,
        file: fileUrl
      });
    } catch (err) {
      console.error("Failed to save changes due to file upload error", err);
    }
  };

  return (
    <div className="modal visible" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="modal-content" style={{ maxWidth: '650px', width: '90%', maxHeight: '85vh', overflowY: 'auto', borderRadius: '12px', padding: '24px', background: '#fff', position: 'relative', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
        
        {/* Close button */}
        <div
          style={{ position: 'absolute', right: '16px', top: '16px', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}
          onClick={onClose}
        >
          <i className="fa-solid fa-xmark"></i>
        </div>

        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', color: '#111827' }}>
          Edit Query (All Fields)
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {editableFields.map((key) => {
              const label = columnMapping[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const val = formData[key] ?? '';
              const isLongText = key === 'comment' || key === 'query_desc' || key === 'feedback' || key === 'reason' || key === 'deviceSpecs' || key === 'content_desc';
              
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: (isLongText || key === 'file') ? 'span 2' : 'span 1' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#4b5563', textTransform: 'capitalize' }}>
                    {label}
                  </label>
                  
                  {key === 'file' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {val ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <a href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline', fontSize: '14px' }}>
                            View current file
                          </a>
                        </div>
                      ) : (
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>No file uploaded</span>
                      )}
                      <input
                        type="file"
                        onChange={(e) => setFileObj(e.target.files[0])}
                        style={{ fontSize: '14px', color: '#4b5563', marginTop: '4px' }}
                      />
                      {fileObj && (
                        <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '500' }}>
                          New file selected: {fileObj.name} (Pending Save)
                        </span>
                      )}
                    </div>
                  ) : key === 'current_course' || key === 'new_course' ? (
                    <InlineSearchableDropdown
                      options={courses}
                      value={val}
                      onChange={(course) => handleChange(key, course)}
                      placeholder={`Select ${label}...`}
                    />
                  ) : isLongText ? (
                    <textarea
                      value={val}
                      onChange={(e) => handleChange(key, e.target.value)}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%', minHeight: '80px', outline: 'none', background: '#fff', color: '#000' }}
                    />
                  ) : (
                    <input
                      type={key === 'amount' ? 'number' : 'text'}
                      value={val}
                      onChange={(e) => handleChange(key, e.target.value)}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%', outline: 'none', background: '#fff', color: '#000' }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', background: '#f3f4f6', color: '#374151', fontWeight: '500' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              style={{ padding: '8px 24px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: uploading ? '#9ca3af' : '#ff8e00', color: '#fff', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {uploading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditQueryModal;
